<script setup lang="ts">
import { defineEmits, defineProps, ref } from "vue";
import { useTranslate } from "@/composables/useTranslate";
import SpinnerLoader from "../Loaders/SpinnerLoader.vue";
import { WorkerBonus } from "@/types";

type Props = {
  isReportDialogOpen: boolean;
  workersBonuses: Array<WorkerBonus>;
  selectedMonth: any;
  selectedYear: any;
  areBonusesValid: boolean;
};

const props = defineProps<Props>();

type Emits = {
  (event: "onClose"): void;
  (event: "decreaseDialogStage"): void;
  (event: "submitReport"): void;
};

const emits = defineEmits<Emits>();
const { t } = useTranslate();

const isLoading = ref(false);

const handleClose = () => {
  emits("onClose");
};
const handleBack = () => {
  emits("decreaseDialogStage");
};
const handleForward = () => {
  isLoading.value = true;
  emits("submitReport");
};
</script>
<template>
  <Transition name="nested">
    <div class="w-full h-screen fixed top-0 left-0 bg-black/75 z-[1000]">
      <div
        class="w-full max-w-[1234px] px-[20px] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-[7px] bg-white z-[1000]"
      >
        <div
          class="flex items-center justify-between py-[16px] mb-[16px] border-b border-[#E3E1EA]"
        >
          <div class="flex items-center">
            <img
              class="w-[24px] h-[24px]"
              src="@/assets/Clipboard_Check.svg"
              alt=""
            />
            <p class="ml-[8px] text-[20px] font-semibold text-[#393840]">
              {{ t("installers.review_bonus_report") }}
              {{ selectedMonth.name }} {{ selectedYear.id }}
            </p>
          </div>
          <img
            class="h-[12px] w-[12px] cursor-pointer"
            src="@/assets/close-icon-2.svg"
            alt=""
            @click="handleClose()"
          />
        </div>

        <div
          class="max-h-[400px] overflow-x-auto border-b border-[#E3E1EA] rounded-tl-[7px] flex flex-col"
        >
          <table class="min-w-full border-separate border-spacing-[0.5px]">
            <thead class="bg-[#F9F8F6]">
              <tr>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_name") }}
                </th>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_email") }}
                </th>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_total_bonus") }}
                </th>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_scaffolding") }}
                </th>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_commercial") }}
                </th>
                <th
                  class="w-1/6 p-[12px] text-left text-[16px] font-semibold text-[#393840] border border-[#E3E1EA] border-b-0"
                >
                  {{ t("installers.review_table_column_bonus_payout") }}
                </th>
              </tr>
            </thead>
            <tbody class="">
              <tr v-for="(workerBonus, index) in workersBonuses" :key="index">
                <td
                  class="p-[12px] text-[16px] text-[#393840] border border-[#E3E1EA]"
                >
                  {{ workerBonus.name }}
                </td>
                <td
                  class="p-[12px] text-[16px] text-[#393840] border border-[#E3E1EA]"
                >
                  {{ workerBonus.email }}
                </td>
                <td
                  class="p-[12px] text-[16px] text-[#393840] border"
                  :class="`${
                    workerBonus.bonusTotal.valid
                      ? 'border-[#E3E1EA]'
                      : 'border-[#ED857F] bg-[#fde1e0]'
                  }`"
                >
                  {{ workerBonus.bonusTotal.value }}€
                </td>
                <td
                  class="p-[12px] text-[16px] text-[#393840] border border-[#E3E1EA]"
                  :class="`${
                    workerBonus.scaffoldingTotal.valid
                      ? 'border-[#E3E1EA]'
                      : 'border-[#ED857F] bg-[#fde1e0]'
                  }`"
                >
                  {{ workerBonus.scaffoldingTotal.value }}€
                </td>
                <td
                  class="p-[12px] text-[16px] text-[#393840] border border-[#E3E1EA]"
                  :class="`${
                    workerBonus.commercialTotal.valid
                      ? 'border-[#E3E1EA]'
                      : 'border-[#ED857F] bg-[#fde1e0]'
                  }`"
                >
                  {{ workerBonus.commercialTotal.value }}€
                </td>
                <td
                  class="p-[12px] text-[16px] text-[#393840] border border-[#E3E1EA]"
                  :class="`${
                    workerBonus.bonusPayoutTotal.valid
                      ? 'border-[#E3E1EA]'
                      : 'border-[#ED857F] bg-[#fde1e0]'
                  }`"
                >
                  {{ workerBonus.bonusPayoutTotal.value }}€
                </td>
              </tr>
            </tbody>
          </table>
          <div
            v-if="!areBonusesValid"
            class="flex items-center gap-[1px] mb-[16px] mt-[6px]"
          >
            <img class="w-3 h-3" src="@/assets/warning-red-rounded.svg" />
            <span class="text-sm text-[#ed857f]">{{
              t("installers.error_upload_again")
            }}</span>
          </div>
        </div>

        <div class="py-[12px] flex items-center justify-between">
          <button
            @click="handleBack()"
            class="min-w-[150px] flex items-center justify-center p-[12px] gap-[8px] text-[16px] text-white font-semibold border-2 border-[#393840] rounded-[7px]"
          >
            <img
              class="w-[18px] h-[18px]"
              src="@/assets/prev-btn-arrow-left.svg"
              alt=""
            />
            <span class="text-[#393840]">{{
              t("installers.back_to_edit")
            }}</span>
          </button>
          <button
            v-if="
              areBonusesValid && workersBonuses && workersBonuses.length > 0
            "
            @click="handleForward()"
            class="w-[150px] flex items-center justify-center p-[12px] gap-[8px] text-[16px] text-white font-semibold bg-[#393840] rounded-[7px]"
          >
            <div v-if="isLoading" class="flex items-center justify-center">
              <SpinnerLoader />
            </div>
            <span v-else>{{ t("global.submit") }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>
